/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { ICommerceApiSettings } from '@msdyn365-commerce/core-internal';
import { DimensionTypes, generateSwatchImageUrl, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import classnames from 'classnames';
import * as React from 'react';

/**
 * Swatch item data.
 */
export interface ISwatchItem {
    value: string;
    itemId: string;
    dimensionType: DimensionTypes;
    colorHexCode?: string;
    imageUrl?: string;
    productImageUrls?: string[];
    isDisabled?: boolean;
    isDefault?: boolean;
}

/**
 * Swatch item configuration.
 */
export interface ISwatchItemProps {
    className: string;
    apiSettings: ICommerceApiSettings;

    isSelected: boolean;
    isSelectionEnabled?: boolean;

    item: ISwatchItem;

    onSelectItem(item: ISwatchItem): void;
}

/**
 * Represents a single item for swatch.
 * @param props - Item configuration.
 * @returns React node.
 */
export const SwatchItemComponent: React.FC<ISwatchItemProps> = (props: ISwatchItemProps) => {
    const { isSelected, className, onSelectItem, item } = props;
    const { itemId, value, colorHexCode, imageUrl } = item;

    const style: React.CSSProperties = {};

    let isColored = false;

    const hasColor = !StringExtensions.isNullOrWhitespace(colorHexCode);
    const hasImage = !StringExtensions.isNullOrWhitespace(imageUrl);

    const selectItem = React.useCallback(() => {
        onSelectItem(item);
    }, [onSelectItem, item]);

    const selectionHandler = props.isSelectionEnabled ? selectItem : undefined;

    if (hasColor) {
        style.backgroundColor = colorHexCode;
        isColored = true;
    } else if (hasImage) {
        const fullImageUrl = generateSwatchImageUrl(imageUrl!, props.apiSettings);
        return (
            <img
                key={`${itemId}-${item.isDisabled ? 'disabled' : 'enabled'}`}
                src={fullImageUrl}
                className={
                    classnames(
                        className,
                        `${className}-${itemId}`,
                        `${className}__image`,
                        isSelected ? `${className}__selected` : `${className}__unselected`,
                        item.isDisabled ? `${className}__disabled` : undefined
                    )
                }
                style={style}
                onClick={selectionHandler}
                onKeyPress={selectionHandler}
                title={value}
                aria-label={value}
                alt={value}
            />
        );
    }

    const shouldDisplayText = !isColored;

    return (
        <button
            key={`${itemId}-${item.isDisabled ? 'disabled' : 'enabled'}`}
            className={
                classnames(
                    className,
                    `${className}-${itemId}`,
                    isSelected ? `${className}__selected` : `${className}__unselected`,
                    isColored ? `${className}__colored` : `${className}__uncolored`,
                    item.isDisabled ? `${className}__disabled` : undefined
                )
            }
            style={style}
            onClick={selectionHandler}
            onKeyPress={selectionHandler}
            title={value}
            aria-label={value}
            disabled={!props.isSelectionEnabled}
        >
            {shouldDisplayText && value}
        </button>
    );
};
